import {DefaultButton, DialogFooter, IDialogFooterStyles, PrimaryButton, Stack, mergeStyleSets} from "@fluentui/react";
import React, {useEffect, useMemo, useReducer, useState} from "react";
import {useFormContext, useWatch} from "react-hook-form";
import CustomPaginate, {PageItem} from "../../../share/CustomPaginate";
import {useCustomForm} from "./hooks/useCustomForm";
import {isMobile} from "react-device-detect";
import {useTranslation} from "react-i18next";
import {useBranding} from "../../../hooks/useBranding";
// MODERNIZATION: named step rail replaces the numeric pager on public link pages.
import FormStepRail from "./FormStepRail";

type FormPaginateProps = {
    pages: number
    currentPage: number
    forcePage: number
    showPagination: boolean
    submitDisable: boolean
    setForcePage: React.Dispatch<React.SetStateAction<number>>
    setCurrentPage: React.Dispatch<React.SetStateAction<number>>
    handleCloseModal: () => void
    onSave: (items?: PageItem[] | undefined) => void
    isWizardPreview: boolean
    data: any
    /** MODERNIZATION: 'link' renders the named step rail instead of the numeric
     *  pager. Optional, so existing call sites are unaffected. */
    docSource?: string
    /** 1-based page numbers with a missed required field (from submit validation);
     *  the step rail pulses these markers red. */
    errorPages?: number[]
}
export default function FormPaginate({
                                         pages, currentPage, forcePage, setForcePage, showPagination, handleCloseModal,
                                         submitDisable, onSave, isWizardPreview, data,
                                         setCurrentPage, docSource, errorPages
                                     }: FormPaginateProps) {
    const {branding} = useBranding()
    const {getValues} = useFormContext()
    const {t} = useTranslation(["common", "wizard", "preview"]);
    const {layoutsRef, triggerPages} = useCustomForm()
    
    const [triggerRender, setTriggerRender] = useState(0)
    const [customPages, setCustomPages] = useState<PageItem[]>(() => {
        return Array.from(Array(pages).keys()).map((page) => {
            return {
                index: page,
                name: `${page + 1}`,
                disabled: triggerPages.get(`${page + 1}`) ?? false
            }
        }) as PageItem[]
    })    
    

    const contentStyles = mergeStyleSets({
        pagination: {
            padding: 0,
            selectors: {
                "ul": {
                    display: "inline-block"
                },
                "li": {
                    fontSize: "12pt",
                    color: branding.theme.palette.themeDarker,
                    display: "inline-block",
                    border: `solid 1px ${branding.theme.palette.themeDarker}`,
                    cursor: "pointer",
                    paddingLeft: "8px",
                    paddingRight: "8px",
                    borderCollapse: "collapse",
                    fontWeight: "500"
                }
            }
        },
        active: {
            backgroundColor: branding.theme.palette.themeDarker,
            selectors: {
                "span": {
                    color: branding.theme.palette.white
                }
            }
        }
    })
       
    /**
     * Force to enable/disable a page
     */
    useEffect(() => {
        if (triggerPages.size > 0) {
            const updatePages = customPages.map((page) => {
                return {
                    index: page.index,
                    name: page.name,
                    disabled: triggerPages.get(`${page.index + 1}`) ?? false
                }
            }) as PageItem[]
            
            setCustomPages(updatePages)
            
            const firstEnablePage = updatePages.find(x => !x.disabled)
            if(firstEnablePage !== undefined) {
                setTriggerRender(prev => prev + 1)
                setForcePage(firstEnablePage.index)
            }
            else {
                setForcePage(-1)
                setCurrentPage(-1)
            }
            
        }
    }, [triggerPages])

    

    const form = useFormContext()

    const onPageClick = (data: any, callback: (result: boolean | number) => void) => {
        if ((data.isPrevious && data.selected === 0) || (data.isNext && data.selected === pages - 1)) {
            callback(false);
        } else if (docSource === 'normal') {
            // /app view: FREE navigation — page through without validation; required
            // fields are validated only on submit (missed pages then pulse red).
            setForcePage(data.nextSelectedPage);
            setCurrentPage(data.nextSelectedPage + 1);
            callback(true);
        } else {
            validateInputs(data.nextSelectedPage, callback)
        }
    }

    const showSubmit = useMemo(() => {
        
        if (submitDisable) return true
        if (pages.toString() === currentPage.toString() || (data !== null && data !== "" && data !== undefined)) {
            return true;
        } else {
            const currentPages = [...customPages]
            const lastPage = currentPages.reverse().find(x => !x.disabled)
            return lastPage?.index === (currentPage - 1)
        }
    }, [currentPage, data, pages,  submitDisable, triggerRender])

    const validateInputs = (pagina: number, callback: (result: boolean | number) => void) => {
            form.handleSubmit(
                () => {
                    setForcePage(pagina);
                    setCurrentPage(pagina + 1);
                    callback(true); // Success
                },
                (error) => {
                    const currentPageKeys = Object.keys(error).filter(
                        (key) =>
                            layoutsRef.current
                                .filter((item) => +item.Page === currentPage && !item.Disabled)
                                .map((control) => control.Id)
                                .includes(key) || key === "_shortcutReference"
                    );
                    if (currentPageKeys.length > 0) {
                        document.getElementById(currentPageKeys[0])?.focus();
                    } else {
                        setForcePage(pagina);
                        setCurrentPage(pagina + 1);
                        callback(pagina); // Error
                    }
                }
            )()
    };
    const onSubmit = () => {
            onSave(customPages)
    }

    /**
     * MODERNIZATION: step-rail navigation.
     * Navigation is FREE — required fields are only validated on submit (which then
     * pulses any page still missing one). So switching pages never blocks; it just
     * moves. (The in-app numeric pager still validates per page via onPageClick.)
     */
    const onStepSelect = (pageIndex: number) => {
        if (pageIndex === currentPage - 1) return
        setForcePage(pageIndex)
        setCurrentPage(pageIndex + 1)
    }

    // The named step rail (progress bar + numbered pages) is used on the public link
    // pages and in the designer's wizard preview (both on every device), and on the
    // in-app modals launched from the dashboard — a normal application, a subform, or a
    // draft edit of either — but only on DESKTOP there, so their pages sit at the top
    // instead of the old bottom pager. On mobile (a phone Panel, not the desktop Modal)
    // those in-app forms keep the original bottom pager, which fits the narrow layout —
    // the top rail's desktop margins/fixed footer don't render well on a phone.
    const useStepRail =
        docSource === 'link' ||
        isWizardPreview ||
        (!isMobile && (docSource === 'normal' || docSource === 'subform'))

    // Next / previous ENABLED page (skipping disabled pages) for the step-rail
    // footer navigation buttons.
    const enabledPages = customPages.filter((p) => !p.disabled)
    const curIdx = currentPage - 1
    const prevPage = [...enabledPages].reverse().find((p) => p.index < curIdx)
    const nextPage = enabledPages.find((p) => p.index > curIdx)

    const actionButtons = !isWizardPreview ? (
        <div className={modalFooter.dialogBtns}>
            {useStepRail && showPagination && (
                <>
                    {/* Hidden on the first page (no previous). */}
                    {prevPage && (
                        <DefaultButton
                            id="btnPreviousPage"
                            tabIndex={499}
                            onClick={() => {
                                // Going back should never be blocked by validation.
                                setForcePage(prevPage.index)
                                setCurrentPage(prevPage.index + 1)
                            }}
                            text={"Previous"}
                            title={"Previous page"}
                        />
                    )}
                    {/* Hidden on the last page (no next). */}
                    {nextPage && (
                        <DefaultButton
                            id="btnNextPage"
                            tabIndex={500}
                            // Validates the current page before advancing, same as the rail.
                            onClick={() => onStepSelect(nextPage.index)}
                            text={"Next"}
                            title={"Next page"}
                        />
                    )}
                </>
            )}
            {showSubmit ? <PrimaryButton id="btnSubmit" onClick={onSubmit} disabled={!showSubmit} tabIndex={501}
                                         text={t("Common.Button.Submit", {ns: "common"})}
                                         title={t("title.btnSubmit", {ns: "wizard"})}/> : null}
            {!isMobile && <DefaultButton
                id="btnCancel"
                tabIndex={502}
                onBlur={() => {
                    document.getElementById("help")?.focus();
                }}
                onClick={handleCloseModal}
                text={t(
                    "Common.Button.Cancel",
                    {ns: "common"}
                )}
                title={t(
                    "Common.Button.Cancel",
                    {ns: "common"}
                )}
            />}
        </div>
    ) : (
        <></>
    )

    /**
     * MODERNIZATION: on public link pages the step rail is emitted as a SIBLING of
     * the footer rather than inside it, so it can sit at the top of the modal
     * while Submit/Cancel stay at the bottom.
     *
     * Both end up as flex children of the <Stack> in CustomForm (FormProvider and
     * the fragments around this component add no DOM), so `.fl-rail-slot` is
     * ordered to the top in modern-form-shell.css.
     */
    if (useStepRail) {
        return <>
            {showPagination ? (
                <div className="fl-rail-slot">
                    <FormStepRail
                        pages={pages}
                        currentPage={currentPage}
                        layouts={layoutsRef.current}
                        pageItems={customPages}
                        onSelectPage={onStepSelect}
                        errorPages={errorPages}
                    />
                </div>
            ) : null}
            {/* The preview has no footer action buttons (actionButtons is empty), so
                skip the fixed footer bar there; the rail circles handle navigation. */}
            {!isWizardPreview && (
                <div className="fl-form-footer">
                    {actionButtons}
                </div>
            )}
        </>
    }

    return <DialogFooter styles={dialogStyles}>
        <div>
            {showPagination ? (
                <Stack horizontal>
                    <Stack horizontal>
                        <CustomPaginate items={customPages} onClick={onPageClick}
                                        errorPages={errorPages}
                                        previousLabel={"<<"}
                                        nextLabel={">>"} breakLabel={"..."} breakClassName={"break-me"}
                                        pageCount={pages}
                                        marginPagesDisplayed={2} pageRangeDisplayed={5}
                                        containerClassName={contentStyles.pagination}
                                        activeClassName={contentStyles.active} pageLinkClassName={"indexTab"}
                                        previousLinkClassName={"indexTab"}
                                        nextLinkClassName={"indexTab"} forcePage={forcePage}/>
                    </Stack>
                </Stack>
            ) : null}
        </div>
        {actionButtons}
    </DialogFooter>


}
const dialogStyles: IDialogFooterStyles = {
    actions: {
        display: "flex",
        justifyContent: "space-between",
        position: isMobile ? "relative" : "absolute",
        width: "100%",
        bottom: 0,
        margin: 0,
    },
    actionsRight: {
        display: "flex",
        justifyContent: "space-between",
        width: "100%",
    },
    action: {
        margin: "2px 20px",
    },
};
const modalFooter = mergeStyleSets({
    dialogBtns: {
        display: "flex",
        gap: "8px",
    },
});